import { type ReactElement, useState } from 'react';
import { Box, Panel, Stack } from '@/ui';
import { settingsPanelSurfaceSx } from '@/ui/composites/settingsPageTypographySx';
import { Button } from '@/ui/primitives/Button';
import {
  ProjectDestructiveDialog,
  type ProjectDestructiveAction,
  type ProjectDestructiveDialogProps,
} from './ProjectDestructiveDialog';
import {
  buildProjectSettingsDestructiveActionButtonSx,
  projectSettingsDestructiveActionRowSx,
  projectSettingsDestructiveDescriptionCellSx,
} from './projectSettingsDestructiveSurfaceSx';
import { ProjectSettingsHelperText } from './ProjectSettingsHelperText';
import type { ProjectDto } from '@flowqa/shared';

export type ProjectDataWipeSectionProps = Omit<
  ProjectDestructiveDialogProps,
  'action' | 'onClose' | 'project'
> & {
  project: ProjectDto | null;
};

export const ProjectDataWipeSection = ({
  project,
  deleteProject,
  wipeProjectData,
  mutationError,
  notify,
  onProjectDeleted,
}: ProjectDataWipeSectionProps): ReactElement => {
  const destructiveActionButtonSx = buildProjectSettingsDestructiveActionButtonSx({
    width: { sm: '132px' },
  });

  const [destructiveAction, setDestructiveAction] = useState<ProjectDestructiveAction | null>(null);

  return (
    <Panel title="Project Deletion" surfaceVariant="settings" sx={settingsPanelSurfaceSx}>
      <Stack gap="xs">
        <Stack gap="md">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            align={{ sm: 'flex-start' }}
            sx={projectSettingsDestructiveActionRowSx}
          >
            <Box sx={projectSettingsDestructiveDescriptionCellSx}>
              <ProjectSettingsHelperText>
                Delete all data associated with this project. Includes all module data. Does not
                delete the project.
              </ProjectSettingsHelperText>
            </Box>
            <Button
              variant="secondary"
              color="error"
              fullWidthOnMobile={false}
              sx={destructiveActionButtonSx}
              disabled={!project || wipeProjectData.isPending || deleteProject.isPending}
              onClick={() => {
                mutationError.clearError();
                setDestructiveAction('deleteData');
              }}
            >
              Delete Data
            </Button>
          </Stack>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            align={{ sm: 'flex-start' }}
            sx={projectSettingsDestructiveActionRowSx}
          >
            <Box sx={projectSettingsDestructiveDescriptionCellSx}>
              <ProjectSettingsHelperText>
                Delete the project from workspace. Includes all data associated with this project.
              </ProjectSettingsHelperText>
            </Box>
            <Button
              variant="secondary"
              color="error"
              fullWidthOnMobile={false}
              sx={destructiveActionButtonSx}
              disabled={!project || deleteProject.isPending || wipeProjectData.isPending}
              onClick={() => {
                mutationError.clearError();
                setDestructiveAction('deleteProject');
              }}
            >
              Delete Project
            </Button>
          </Stack>
        </Stack>
        {project && destructiveAction ? (
          <ProjectDestructiveDialog
            action={destructiveAction}
            project={project}
            deleteProject={deleteProject}
            wipeProjectData={wipeProjectData}
            mutationError={mutationError}
            notify={notify}
            onClose={() => {
              setDestructiveAction(null);
            }}
            onProjectDeleted={onProjectDeleted}
          />
        ) : null}
      </Stack>
    </Panel>
  );
};
